<script setup lang="ts">
import { X } from 'lucide-vue-next'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { addYears } from 'date-fns'
import Tools from '@/utils/tools'

interface Props {
	mode?: 'generator' | 'tweak'
}

const props = withDefaults(defineProps<Props>(), {
	mode: 'generator',
})

const { t } = useI18n()
const tour = [
	{
		element: 'body',
		mode: 'tweak',
		popover: {
			title: t('product_tour.tweak_mode.title'),
			content: t('product_tour.tweak_mode.content'),
		},
	},
	{
		element: '#generator-style',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_style.title'),
			content: t('product_tour.generator_style.content'),
		},
	},
	{
		element: '#positive-prompt',
		mode: 'both',
		popover: {
			title: t('product_tour.positive_prompt.title'),
			content: t('product_tour.positive_prompt.content'),
		},
	},
	{
		element: '#negative-prompt',
		mode: 'both',
		popover: {
			title: t('product_tour.negative_prompt.title'),
			content: t('product_tour.negative_prompt.content'),
		},
	},
	{
		element: '#generator-modifiers',
		mode: 'both',
		popover: {
			title: t('product_tour.generator_boosters.title'),
			content: t('product_tour.generator_boosters.content'),
			footer: t('product_tour.generator_boosters.footer'),
		},
	},
	{
		element: '#generator-resolution',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_resolution.title'),
			content: t('product_tour.generator_resolution.content'),
			footer: t('product_tour.generator_resolution.footer'),
		},
	},
	{
		element: '#generator-format',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_format.title'),
			content: t('product_tour.generator_format.content'),
			footer: t('product_tour.generator_format.footer'),
		},
	},
	{
		element: '#generator-creativity',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_creativity.title'),
			content: t('product_tour.generator_creativity.content'),
			footer: t('product_tour.generator_creativity.footer'),
		},
	},
	{
		element: '#generator-face-restore',
		mode: 'both',
		popover: {
			title: t('generator.auto_face_restore'),
			content: t('generator.info_auto_face_restore'),
		},
	},
	{
		element: '#generator-less-nsfw',
		mode: 'both',
		popover: {
			title: t('generator.less_nsfw'),
			content: t('generator.info_less_nsfw'),
		},
	},
	{
		element: '#generator-upscale',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_upscale.title'),
			content: t('product_tour.generator_upscale.content'),
			footer: t('product_tour.generator_upscale.footer'),
		},
	},
	{
		element: '#generator-fastlane',
		mode: 'both',
		popover: {
			title: t('product_tour.generator_fastlane.title'),
			content: t('product_tour.generator_fastlane.content'),
			footer: t('product_tour.generator_fastlane.footer'),
		},
	},
	{
		element: '#generator-slowmode',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_slowmode.title'),
			content: t('product_tour.generator_slowmode.content'),
			footer: t('product_tour.generator_slowmode.footer'),
		},
	},
	{
		element: '#generator-private',
		mode: 'generator',
		popover: {
			title: t('product_tour.generator_private.title'),
			content: t('product_tour.generator_private.content'),
			footer: t('product_tour.generator_private.footer'),
		},
	},
	{
		element: '#generator-seed',
		mode: 'both',
		popover: {
			title: t('product_tour.generator_seed.title'),
			content: t('product_tour.generator_seed.content'),
			footer: t('product_tour.generator_seed.footer'),
		},
	},
]

const step = ref(-1)
const activeElement = ref<HTMLElement | null>(null)
const activeStep = ref<any>()

// Filter tour steps based on current mode
const filteredTour = computed(() =>
	tour.filter(step => step.mode === 'both' || step.mode === props.mode),
)

// Get cookie name based on mode
const getCookieName = () => `product-tour-${props.mode}`

function scrollParent(el: HTMLElement): HTMLElement | Window {
	let parent: HTMLElement | null = el
	while (parent) {
		const style = getComputedStyle(parent)
		if (/(auto|scroll)/.test(style.overflowY))
			return parent
		parent = parent.parentElement
	}
	return window
}

function scrollToElement(): void {
	// activeElement.value.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' })
	if (!activeElement.value)
		return
	const parentElement = scrollParent(activeElement.value)
	const elementRect = activeElement.value.getBoundingClientRect()

	if (parentElement instanceof Window) {
		window.scrollTo({
			top: window.scrollY + elementRect.top - window.innerHeight / 2 + elementRect.height / 2,
			behavior: 'instant',
		})
	}
	else {
		const parentRect = parentElement.getBoundingClientRect()
		const scrollTop
		= parentElement.scrollTop
		+ (elementRect.top - parentRect.top)
		- parentElement.clientHeight / 2
		+ elementRect.height / 2

		parentElement.scrollTo({ top: scrollTop, behavior: 'instant' })
	}
}

function moveNextStep() {
	step.value++

	if (step.value < filteredTour.value.length) {
		activeStep.value = filteredTour.value[step.value]
		const element = document.querySelector(filteredTour.value[step.value].element) as HTMLElement
		if (element) {
			activeElement.value = element
			scrollToElement()
		}
		else {
			// Skip to next step if element doesn't exist
			moveNextStep()
		}
	}
}

function movePreviousStep() {
	step.value--

	if (step.value >= 0) {
		activeStep.value = filteredTour.value[step.value]
		const element = document.querySelector(filteredTour.value[step.value].element) as HTMLElement
		if (element) {
			activeElement.value = element
			scrollToElement()
		}
		else {
			// Skip to previous step if element doesn't exist
			movePreviousStep()
		}
	}
	else {
		step.value = 0
	}
}

function stopTour() {
	document.body.classList.remove('overflow-hidden')
	step.value = -1
	Tools.setCookie(getCookieName(), 'true', addYears(new Date(), 1), `.${import.meta.env.VITE_DOMAIN}`, '/')
}

const maskStyle = computed(() => {
	if (!activeElement.value) {
		return {
			'-webkit-mask-image': 'none',
		}
	}

	const { width, height, left, top } = activeElement.value.getBoundingClientRect()

	return {
		'-webkit-mask-image': 'linear-gradient(#000 0 0), linear-gradient(#000 0 0)',
		'-webkit-mask-composite': 'destination-out',
		'mask-composite': 'exclude',
		'-webkit-mask-position': `0 0, ${left - 5}px ${top - 5}px`,
		'-webkit-mask-size': `100% 100%, ${width + 10}px ${height + 10}px`,
		'-webkit-mask-repeat': 'no-repeat',
	}
})

onMounted(async () => {
	const goTour = Tools.getCookie(getCookieName())
	if (goTour !== 'true') {
		await nextTick()
		document.body.classList.add('overflow-hidden')
		setTimeout(() => {
			if (filteredTour.value.length > 0) {
				const firstElement = document.querySelector(filteredTour.value[0].element) as HTMLElement
				if (firstElement) {
					activeElement.value = firstElement
					step.value++
				}
			}
		}, 200)
	}
})
</script>

<template>
	<Teleport to="body">
		<div
			v-if="step >= 0"
			class="fixed top-0 left-0 w-[100dvw] h-[100dvh] z-[20] bg-slate-950/75"
			:style="maskStyle"
		/>
		<div
			v-if="step >= 0"
			class="fixed bottom-6 xl:top-[100px] xl:bottom-auto left-1/2 -translate-x-1/2 w-[90%] max-w-[375px] p-3 bg-white !text-black z-[21] rounded-xl"
		>
			<span class="font-bold flex justify-between">
				{{ filteredTour[step].popover.title }}
				<X
					class="lucide-button-icon-xs hover:text-slate-400 cursor-pointer duration-300"
					@click="stopTour"
				/>
			</span>
			<p class="flex flex-col gap-1 my-2">
				{{ filteredTour[step].popover.content }}
				<span class="text-slate-500 text-xsp">{{ filteredTour[step].popover.footer }}</span>
			</p>
			<div class="flex flex-nowrap items-center justify-between gap-2 w-full">
				<span class="text-slate-600 text-xsp"> {{ step + 1 }} of {{ filteredTour.length }}</span>
				<div class="flex flex-nowrap items-center justify-end gap-2">
					<Button
						class="button-purple !gap-0 !px-2 !py-1"
						:label="t('common.prev')"
						icon="pi pi-angle-left"
						:disabled="!step"
						@click="movePreviousStep"
					/>
					<Button
						v-if="step !== filteredTour.length - 1"
						class="button-purple !gap-0 !px-2 !py-1"
						:label="t('common.next')"
						icon="pi pi-angle-right"
						icon-pos="right"
						@click="moveNextStep"
					/>
					<Button
						v-if="step === filteredTour.length - 1"
						class="button-purple !gap-0 !px-2 !py-1"
						label="Done"
						icon="pi pi-check"
						icon-pos="right"
						@click="stopTour"
					/>
				</div>
			</div>
		</div>
	</Teleport>
</template>
